import React, { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import { GROUP_TIMELINE_FAMILY_LABELS } from '../../utils/groupTimeline';
import {
  conflictBadgeTone,
  hasConflictIssues,
  type ConflictBadgeToneScope,
  type GroupTimelineConflictIssues,
  type GroupTimelineConflictPair,
} from '../../utils/groupTimelineConflicts';
import './GroupTimelineConflictBadge.css';

function formatShortDay(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const DAY_MS = 86400000;

function axisPct(value: number, min: number, span: number): number {
  return ((value - min) / span) * 100;
}

type SnapSide = {
  key: string;
  label: string;
  startDate: Date;
  endDate: Date;
};

function groupConflictPairs(pairs: GroupTimelineConflictPair[]): Array<{
  key: string;
  family: GroupTimelineConflictPair['family'];
  supplierName: string;
  pairs: GroupTimelineConflictPair[];
}> {
  const groups: Array<{
    key: string;
    family: GroupTimelineConflictPair['family'];
    supplierName: string;
    pairs: GroupTimelineConflictPair[];
  }> = [];
  const index = new Map<string, number>();
  for (const pair of pairs) {
    const key = `${pair.family}:${pair.supplierOrgId || 'install'}`;
    const existing = index.get(key);
    if (existing !== undefined) {
      groups[existing].pairs.push(pair);
      continue;
    }
    index.set(key, groups.length);
    groups.push({
      key,
      family: pair.family,
      supplierName: pair.supplierName,
      pairs: [pair],
    });
  }
  return groups;
}

function uniqueSnapSides(pairs: GroupTimelineConflictPair[]): SnapSide[] {
  const byId = new Map<string, SnapSide>();
  for (const pair of pairs) {
    for (const side of [pair.a, pair.b]) {
      if (byId.has(side.taskId)) continue;
      byId.set(side.taskId, {
        key: side.taskId,
        label: side.projectLabel,
        startDate: side.startDate,
        endDate: side.endDate,
      });
    }
  }
  return Array.from(byId.values()).sort((a, b) => {
    const byStart = a.startDate.getTime() - b.startDate.getTime();
    if (byStart !== 0) return byStart;
    return a.label.localeCompare(b.label);
  });
}

function ConflictTimeline({ pairs }: { pairs: GroupTimelineConflictPair[] }) {
  const sides = uniqueSnapSides(pairs);
  if (sides.length === 0) return null;
  const min = Math.min(
    ...sides.map((s) => s.startDate.getTime()),
    ...pairs.map((p) => p.overlapStart.getTime()),
  );
  // Inclusive end day so Oct 7 fills that calendar day, matching the captions.
  const max = Math.max(
    ...sides.map((s) => s.endDate.getTime()),
    ...pairs.map((p) => p.overlapEnd.getTime()),
  ) + DAY_MS;
  const span = Math.max(max - min, DAY_MS);
  const bar = (start: Date, end: Date) => {
    const startT = start.getTime();
    const endT = end.getTime() + DAY_MS;
    return {
      left: `${axisPct(startT, min, span)}%`,
      width: `${Math.max(axisPct(endT, min, span) - axisPct(startT, min, span), 2.5)}%`,
    };
  };

  return (
    <div className="pm-gt-conflict-snap">
      <div className="pm-gt-conflict-snap-row pm-gt-conflict-snap-row--axis" aria-hidden>
        <span className="pm-gt-conflict-snap-label" />
        <div className="pm-gt-conflict-snap-track pm-gt-conflict-snap-axis">
          {pairs.map((pair) => (
            <span
              key={`${pair.a.taskId}:${pair.b.taskId}:${pair.overlapStart.getTime()}`}
              className="pm-gt-conflict-snap-overlap"
              style={bar(pair.overlapStart, pair.overlapEnd)}
            />
          ))}
        </div>
      </div>
      {sides.map((side, i) => (
        <div key={side.key} className="pm-gt-conflict-snap-row">
          <span className="pm-gt-conflict-snap-label">{side.label}</span>
          <div className="pm-gt-conflict-snap-track">
            <span
              className={`pm-gt-conflict-snap-bar${i % 2 === 1 ? ' pm-gt-conflict-snap-bar--b' : ''}`}
              style={bar(side.startDate, side.endDate)}
            />
          </div>
        </div>
      ))}
      <div className="pm-gt-conflict-snap-dates">
        <span className="pm-gt-conflict-snap-label" aria-hidden />
        <span className="pm-gt-conflict-snap-date-range">
          <span>{formatShortDay(new Date(min))}</span>
          <span>{formatShortDay(new Date(max - DAY_MS))}</span>
        </span>
      </div>
      <p className="pm-gt-conflict-snap-legend">Tan band = dates both jobs need this supplier</p>
      <ul className="pm-gt-conflict-snap-overlaps">
        {pairs.map((pair) => (
          <li key={`${pair.a.taskId}:${pair.b.taskId}`}>
            {pair.a.projectLabel}
            {' + '}
            {pair.b.projectLabel}
            {' share '}
            {pair.overlapDays} {pair.overlapDays === 1 ? 'day' : 'days'}
            {', '}
            {formatShortDay(pair.overlapStart)}–{formatShortDay(pair.overlapEnd)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function placeCard(anchor: HTMLElement, card?: HTMLElement | null): { top: number; left: number } {
  const rect = anchor.getBoundingClientRect();
  const width = card?.offsetWidth || 320;
  const height = card?.offsetHeight || 0;
  const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
  let top = rect.bottom + 6;
  if (height && top + height > window.innerHeight - 8) {
    top = Math.max(8, rect.top - height - 6);
  }
  return { top, left };
}

interface GroupTimelineConflictBadgeProps {
  issues: GroupTimelineConflictIssues;
  /** Screen-reader prefix, e.g. “Procurement” or a project label. */
  label?: string;
  /** Limit red/yellow urgency to this project or task’s planned start. */
  toneScope?: ConflictBadgeToneScope;
  onShowConflicts?: (issues: GroupTimelineConflictIssues) => void;
}

const GroupTimelineConflictBadge: React.FC<GroupTimelineConflictBadgeProps> = ({
  issues,
  label,
  toneScope,
  onShowConflicts,
}) => {
  const cardId = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const show = useCallback(() => {
    if (!anchorRef.current) return;
    setCoords(placeCard(anchorRef.current));
    setOpen(true);
  }, []);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current || !cardRef.current) return;
    setCoords(placeCard(anchorRef.current, cardRef.current));
  }, [open, issues]);

  const hide = useCallback(() => setOpen(false), []);

  if (!hasConflictIssues(issues)) return null;

  const count = issues.missing.length + issues.pairs.length;
  const tone = conflictBadgeTone(issues, new Date(), toneScope);
  const missingSupplier = issues.missing.length > 0;
  const kind = tone === 'warning' ? 'warning' : 'issue';
  const aria = [
    label ? `${label}:` : null,
    `${count} timeline ${count === 1 ? kind : `${kind}s`}`,
    missingSupplier ? 'Supplier missing.' : null,
    'Show only these tasks.',
  ].filter(Boolean).join(' ');

  return (
    <>
      <span
        ref={anchorRef}
        className={`pm-gt-conflict-badge pm-gt-conflict-badge--${tone}${missingSupplier ? ' pm-gt-conflict-badge--missing' : ''}`}
        tabIndex={0}
        role="button"
        aria-label={aria}
        aria-describedby={open ? cardId : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          hide();
          onShowConflicts?.(issues);
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          e.stopPropagation();
          hide();
          onShowConflicts?.(issues);
        }}
      >
        <AlertTriangle size={12} strokeWidth={2.4} aria-hidden />
        {missingSupplier ? (
          <sup className="pm-gt-conflict-badge-s" aria-hidden>S</sup>
        ) : null}
      </span>
      {open && createPortal(
        <div
          ref={cardRef}
          id={cardId}
          className="pm-gt-conflict-card"
          role="tooltip"
          style={{ top: coords.top, left: coords.left }}
        >
          {issues.missing.map((miss) => (
            <div key={`${miss.family}:${miss.taskId}`} className="pm-gt-conflict-row">
              <div className="pm-gt-conflict-row-kicker">
                {GROUP_TIMELINE_FAMILY_LABELS[miss.family]}
              </div>
              <div className="pm-gt-conflict-row-title">{miss.projectLabel}</div>
              <div className="pm-gt-conflict-row-meta">{miss.taskTitle}</div>
              <div className="pm-gt-conflict-row-warn">
                {miss.uncoveredStructureNames?.length
                  ? `No supplier for ${miss.uncoveredStructureNames.join(', ')}.`
                  : 'No supplier assigned.'}
              </div>
            </div>
          ))}
          {groupConflictPairs(issues.pairs).map((group) => (
            <div key={group.key} className="pm-gt-conflict-row">
              <div className="pm-gt-conflict-row-kicker">
                {GROUP_TIMELINE_FAMILY_LABELS[group.family]} · {group.supplierName}
              </div>
              <ConflictTimeline pairs={group.pairs} />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};

export default GroupTimelineConflictBadge;
